import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, { lazy, Suspense } from "react";

import ErrorBoundary from "~/components/ErrorBoundary";
import Skeleton from "~/components/loaders/Skeleton";

import blockNameTransform from "~/utilities/blockNameTransform";
import { useTranslationContext } from "../context/TranslationContext";

const AcfAccordionsBlock = lazy(() => import("~/blocks/acf/AccordionsBlock"));
const AcfAdFullBlock = lazy(() => import("~/blocks/acf/AdFullBlock"));
const AcfAdWithDropdownBlock = lazy(
    () => import("~/blocks/acf/AdWithDropdownBlock"),
);
const AcfAlertBlock = lazy(() => import("~/blocks/acf/AlertBlock"));
const AcfCardLinksBlock = lazy(() => import("~/blocks/acf/CardLinksBlock"));
const AcfContactBlock = lazy(() => import("~/blocks/acf/ContactBlock"));
const AcfContentBlock = lazy(() => import("~/blocks/acf/ContentBlock"));
const AcfCookieTypesBlock = lazy(() => import("~/blocks/acf/CookieTypesBlock"));
const AcfCrossReferenceSearchBlock = lazy(
    () => import("~/blocks/acf/CrossReferenceSearchBlock"),
);
const AcfCtaSectionsBlock = lazy(() => import("~/blocks/acf/CtaSectionsBlock"));
const AcfDocumentBlock = lazy(() => import("~/blocks/acf/DocumentBlock"));
const AcfDocumentLibraryBlock = lazy(
    () => import("~/blocks/acf/DocumentLibraryBlock"),
);
const AcfEmailVerificationBlock = lazy(
    () => import("~/blocks/acf/EmailVerificationBlock"),
);
const AcfEventsBlock = lazy(() => import("~/blocks/acf/EventsBlock"));
const AcfFeaturedKcLeadershipBlock = lazy(
    () => import("~/blocks/acf/FeaturedKcLeadershipBlock"),
);
const AcfFeaturedNewsBlock = lazy(
    () => import("~/blocks/acf/FeaturedNewsBlock"),
);
const AcfFeaturedProductsCarouselBlock = lazy(
    () => import("~/blocks/acf/FeaturedProductsCarouselBlock"),
);
const AcfFeaturedResourcesBlock = lazy(
    () => import("~/blocks/acf/FeaturedResourcesBlock"),
);
const AcfFormBlock = lazy(() => import("~/blocks/acf/FormBlock"));
const AcfFullWidthCtaBlock = lazy(
    () => import("~/blocks/acf/FullWidthCtaBlock"),
);
const AcfGraphicWithHotspotsBlock = lazy(
    () => import("~/blocks/acf/GraphicWithHotspotsBlock"),
);
const AcfGroupOfLinksBlock = lazy(
    () => import("~/blocks/acf/GroupOfLinksBlock"),
);
const AcfHeroBlock = lazy(() => import("~/blocks/acf/HeroBlock"));
const AcfHeroSliderBlock = lazy(() => import("~/blocks/acf/HeroSliderBlock"));
const AcfImageSliderBlock = lazy(() => import("~/blocks/acf/ImageSliderBlock"));
const AcfInfoCardsBlock = lazy(() => import("~/blocks/acf/InfoCardsBlock"));
const AcfIntroductionBlockBlock = lazy(
    () => import("~/blocks/acf/IntroductionBlockBlock"),
);
const AcfJsEmbedBlock = lazy(() => import("~/blocks/acf/JsEmbedBlock"));
const AcfJumpLinksBlock = lazy(() => import("~/blocks/acf/JumpLinksBlock"));
const AcfKnowledgeCenterBlock = lazy(
    () => import("~/blocks/acf/KnowledgeCenterBlock"),
);
const AcfKnowledgeCenterFilterBlock = lazy(
    () => import("~/blocks/acf/KnowledgeCenterFilterBlock"),
);
const AcfLatestNewsBlock = lazy(() => import("~/blocks/acf/LatestNewsBlock"));
const AcfLightboxImageBlock = lazy(
    () => import("~/blocks/acf/LightboxImageBlock"),
);
const AcfLinkSectionBlock = lazy(() => import("~/blocks/acf/LinkSectionBlock"));
const AcfLottieBlock = lazy(() => import("~/blocks/acf/LottieBlock"));
const AcfMathjaxBlock = lazy(() => import("~/blocks/acf/MathjaxBlock"));
const AcfMpbBuilderBlock = lazy(
    () => import("~/blocks/acf/MaterialsPartsBuilderBlock"),
);
const AcfNewsBlockBlock = lazy(() => import("~/blocks/acf/NewsBlockBlock"));
const AcfNewsCardCarouselBlock = lazy(
    () => import("~/blocks/acf/NewsCardCarouselBlock"),
);
const AcfPageHeader404Block = lazy(
    () => import("~/blocks/acf/PageHeader404Block"),
);
const AcfPageHeaderBlock = lazy(() => import("~/blocks/acf/PageHeaderBlock"));
const AcfPartnersBlock = lazy(() => import("~/blocks/acf/PartnersBlock"));
const AcfPastEventsBlock = lazy(() => import("~/blocks/acf/PastEventsBlock"));
const AcfProductCardSectionBlock = lazy(
    () => import("~/blocks/acf/ProductCardSectionBlock"),
);
const AcfProductTableBlock = lazy(
    () => import("~/blocks/acf/ProductTableBlock"),
);
const AcfQuoteBlock = lazy(() => import("~/blocks/acf/QuoteBlock"));
const AcfReferenceDesignCardsBlock = lazy(
    () => import("~/blocks/acf/ReferenceDesignCardsBlock"),
);

const AcfReferenceDesignsTableBlock = lazy(
    () => import("~/blocks/acf/ReferenceDesignsTableBlock"),
);
const AcfResourcesBlockBlock = lazy(
    () => import("~/blocks/acf/ResourcesBlockBlock"),
);

const AcfShoppingCartBlock = lazy(
    () => import("~/blocks/acf/ShoppingCartBlock"),
);
const AcfSidebarTabsBlock = lazy(() => import("~/blocks/acf/SidebarTabsBlock"));
const AcfSidebarTitleSectionBlock = lazy(
    () => import("~/blocks/acf/SidebarTitleSectionBlock"),
);
const AcfSpeedfitBlock = lazy(() => import("~/blocks/acf/SpeedfitBlock"));
const AcfStatCardsBlock = lazy(() => import("~/blocks/acf/StatCardsBlock"));
const AcfTabbedAdBlock = lazy(() => import("~/blocks/acf/TabbedAdBlock"));
const AcfTabSectionBlock = lazy(() => import("~/blocks/acf/TabSectionBlock"));
const AcfTestimonialsBlock = lazy(
    () => import("~/blocks/acf/TestimonialsBlock"),
);
const AcfThreeColumnCardsBlock = lazy(
    () => import("~/blocks/acf/ThreeColumnCardsBlock"),
);
const AcfTimelineBlock = lazy(() => import("~/blocks/acf/TimelineBlock"));
const AcfTwitterTimelinBlock = lazy(
    () => import("~/blocks/acf/TwitterTimelineBlock"),
);
const AcfUpcomingEventsBlock = lazy(
    () => import("~/blocks/acf/UpcomingEventsBlock"),
);
const AcfVideoAdBlock = lazy(() => import("~/blocks/acf/VideoAdBlock"));
const AcfVideoEmbedBlock = lazy(() => import("~/blocks/acf/VideoEmbedBlock"));
const AcfVideoPlaylistBlock = lazy(
    () => import("~/blocks/acf/VideoPlaylistBlock"),
);
const AcfWhereToBuyBlock = lazy(() => import("~/blocks/acf/WhereToBuyBlock"));
const CoreButtonBlock = lazy(() => import("~/blocks/core/ButtonBlock"));
const CoreButtonsBlock = lazy(() => import("~/blocks/core/ButtonsBlock"));
const CoreCodeBlock = lazy(() => import("~/blocks/core/CodeBlock"));
const CoreColumnsBlock = lazy(() => import("~/blocks/core/ColumnsBlock"));
const CoreEmbedBlock = lazy(() => import("~/blocks/core/EmbedBlock"));
const CoreFileBlock = lazy(() => import("~/blocks/core/FileBlock"));
const CoreFreeformBlock = lazy(() => import("~/blocks/core/FreeformBlock"));
const CoreGroupBlock = lazy(() => import("~/blocks/core/GroupBlock"));
const CoreHeadingBlock = lazy(() => import("~/blocks/core/HeadingBlock"));
const CoreHtmlBlock = lazy(() => import("~/blocks/core/HtmlBlock"));
const CoreImageBlock = lazy(() => import("~/blocks/core/ImageBlock"));
const CoreListBlock = lazy(() => import("~/blocks/core/ListBlock"));
const CoreMediaTextBlock = lazy(() => import("~/blocks/core/MediaTextBlock"));
const CoreParagraphBlock = lazy(() => import("~/blocks/core/ParagraphBlock"));
const CorePullquoteBlock = lazy(() => import("~/blocks/core/PullquoteBlock"));
const CoreQuoteBlock = lazy(() => import("~/blocks/core/QuoteBlock"));
const CoreSearchBlock = lazy(() => import("~/blocks/core/SearchBlock"));
const CoreSeparatorBlock = lazy(() => import("~/blocks/core/SeparatorBlock"));
const CoreSpacerBlock = lazy(() => import("~/blocks/core/SpacerBlock"));
const CoreTableBlock = lazy(() => import("~/blocks/core/TableBlock"));
const CoreVideoBlock = lazy(() => import("~/blocks/core/VideoBlock"));

const blocksOptions = {
    AcfAccordionsBlock,
    AcfAdFullBlock,
    AcfAdWithDropdownBlock,
    AcfAlertBlock,
    AcfCardLinksBlock,
    AcfContactBlock,
    AcfContentBlock,
    AcfCookieTypesBlock,
    AcfCrossReferenceSearchBlock,
    AcfCtaSectionsBlock,
    AcfDocumentBlock,
    AcfDocumentLibraryBlock,
    AcfEmailVerificationBlock,
    AcfEventsBlock,
    AcfFeaturedKcLeadershipBlock,
    AcfFeaturedNewsBlock,
    AcfFeaturedProductsCarouselBlock,
    AcfFeaturedResourcesBlock,
    AcfFormBlock,
    AcfFullWidthCtaBlock,
    AcfGraphicWithHotspotsBlock,
    AcfGroupOfLinksBlock,
    AcfHeroBlock,
    AcfHeroSliderBlock,
    AcfImageSliderBlock,
    AcfInfoCardsBlock,
    AcfIntroductionBlockBlock,
    AcfJsEmbedBlock,
    AcfJumpLinksBlock,
    AcfKnowledgeCenterBlock,
    AcfKnowledgeCenterFilterBlock,
    AcfLatestNewsBlock,
    AcfLightboxImageBlock,
    AcfLinkSectionBlock,
    AcfLottieBlock,
    AcfMathjaxBlock,
    AcfMpbBuilderBlock,
    AcfNewsBlockBlock,
    AcfNewsCardCarouselBlock,
    AcfPageHeader404Block,
    AcfPageHeaderBlock,
    AcfPartnersBlock,
    AcfPastEventsBlock,
    AcfProductCardSectionBlock,
    AcfProductTableBlock,
    AcfQuoteBlock,
    AcfReferenceDesignCardsBlock,
    AcfReferenceDesignsTableBlock,
    AcfResourcesBlockBlock,
    AcfShoppingCartBlock,
    AcfSidebarTabsBlock,
    AcfSidebarTitleSectionBlock,
    AcfSpeedfitBlock,
    AcfStatCardsBlock,
    AcfTabbedAdBlock,
    AcfTabSectionBlock,
    AcfTestimonialsBlock,
    AcfThreeColumnCardsBlock,
    AcfTimelineBlock,
    AcfTwitterTimelinBlock,
    AcfUpcomingEventsBlock,
    AcfVideoAdBlock,
    AcfVideoEmbedBlock,
    AcfVideoPlaylistBlock,
    AcfWhereToBuyBlock,
    CoreButtonBlock,
    CoreButtonsBlock,
    CoreCodeBlock,
    CoreColumnsBlock,
    CoreEmbedBlock,
    CoreFileBlock,
    CoreFreeformBlock,
    CoreGroupBlock,
    CoreHeadingBlock,
    CoreHtmlBlock,
    CoreImageBlock,
    CoreListBlock,
    CoreMediaTextBlock,
    CoreParagraphBlock,
    CorePullquoteBlock,
    CoreQuoteBlock,
    CoreSearchBlock,
    CoreSeparatorBlock,
    CoreSpacerBlock,
    CoreTableBlock,
    CoreVideoBlock,
};

const Block = ({ attrs, name, id, ...props }) => {
    let TheBlock = null;

    const blockName = blockNameTransform(name);

    if (Object.keys(blocksOptions).includes(blockName)) {
        TheBlock = blocksOptions[blockName];
    }

    return TheBlock ? (
        <ErrorBoundary>
            <Suspense fallback={<Skeleton variant="block" />}>
                <TheBlock {...{ attrs }} {...props} blockName={name} />
            </Suspense>
        </ErrorBoundary>
    ) : (
        <div>{`Unknown Block ${name}`}</div>
    );
};

Block.propTypes = {
    attrs: PropTypes.shape({}),
    name: PropTypes.string.isRequired,
};

const BlockList = ({
    blocks = [],
    translations = [],
    uri = "",
    crumbs = [],
    isKCArticle = false,
    urlPermutations,
}) => {
    const { activeCode } = useTranslationContext();

    if (isEmpty(blocks)) {
        return <div>No Blocks On Page</div>;
    }

    return blocks
        .filter((block) => block)
        .filter(({ name }) => name)
        .map((block, index) => (
            <Block
                {...block}
                {...{
                    languageCode: activeCode,
                    translations,
                    uri,
                    crumbs,
                    isKCArticle,
                    urlPermutations,
                }}
                id={`${block.name}-${index}`}
                key={`${block.name}-${index}`}
            />
        ));
};

BlockList.propTypes = {
    blocks: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        }),
    ),
};

export default BlockList;
